import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useNavigate, Navigate, useParams } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Header } from './components/Header';
import { SplitHeroSection } from './components/SplitHeroSection';
// Legacy import - uncomment to restore original hero section
// import { HeroSectionWithModalLegacy } from './components/HeroSectionWithModalLegacy';
import { HowItWorksSection } from './components/HowItWorksSection';
import GroupBuyPromo from './components/GroupBuyPromo';
import { ServicesSection } from './components/ServicesSection';
import { BenefitsSection } from './components/BenefitsSection';
import { TestimonialsSection } from './components/TestimonialsSection';
import { FAQSection } from './components/FAQSection';
import { Footer } from './components/Footer';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import PasswordGate from './components/auth/PasswordGate';
import { useWebsiteContent } from './hooks/useWebsiteContent';
import { useAuth } from './hooks/useAuth';
import { Toaster } from './components/ui/toaster';
import { useNotifications } from './hooks/useNotifications';
import { useToast } from './hooks/use-toast';
import { FeatureFlagProvider } from './context/FeatureFlagContext';
import { CreditsProvider } from './hooks/useCreditsEnabled';
import HotPostcodesTicker from './components/public/HotPostcodesTicker';
import ProviderSearchSection from './components/public/ProviderSearchSection';

const lazyNamed = (loader, exportName) =>
  React.lazy(() => loader().then((module) => ({ default: module[exportName] })));

// Route-level splitting keeps dashboards, editors, auth, and public detail
// pages out of the homepage bundle.
const ContactPage = lazyNamed(() => import('./components/ContactPage'), 'ContactPage');
const CustomerDashboard = lazyNamed(() => import('./components/dashboard/CustomerDashboard'), 'CustomerDashboard');
const ProviderDashboard = lazyNamed(() => import('./components/dashboard/ProviderDashboard'), 'ProviderDashboard');
const ProjectDetailsPage = lazyNamed(() => import('./components/dashboard/ProjectDetailsPage'), 'ProjectDetailsPage');
const ProviderProjectDetailsPage = lazyNamed(() => import('./components/dashboard/ProviderProjectDetailsPage'), 'ProviderProjectDetailsPage');
const CustomerProjectDetailsPage = lazyNamed(() => import('./components/dashboard/CustomerProjectDetailsPage'), 'CustomerProjectDetailsPage');
const AdminDashboard = lazyNamed(() => import('./components/dashboard/AdminDashboard'), 'AdminDashboard');
const ProviderProfile = lazyNamed(() => import('./components/provider/ProviderProfile'), 'ProviderProfile');
const SigninPage = lazyNamed(() => import('./components/auth/SigninPage'), 'SigninPage');
const SignupPage = lazyNamed(() => import('./components/auth/SignupPage'), 'SignupPage');
const ForgotPasswordPage = lazyNamed(() => import('./components/auth/ForgotPasswordPage'), 'ForgotPasswordPage');
const ResetPasswordPage = lazyNamed(() => import('./components/auth/ResetPasswordPage'), 'ResetPasswordPage');
const OtpVerificationPage = lazyNamed(() => import('./components/auth/OtpVerificationPage'), 'OtpVerificationPage');
const PartnerPage = lazyNamed(() => import('./components/PartnerPage'), 'PartnerPage');
const GroupBuyInfoPage = lazyNamed(() => import('./components/GroupBuyInfoPage'), 'GroupBuyInfoPage');
const ShareLinkPage = lazyNamed(() => import('./components/share/ShareLinkPage'), 'ShareLinkPage');
const PostcodeSharePage = lazyNamed(() => import('./components/share/PostcodeSharePage'), 'PostcodeSharePage');

const ProviderMembershipPlans = React.lazy(() => import('./components/provider/ProviderMembershipPlans'));
const CreditPurchasePage = React.lazy(() => import('./components/provider/CreditPurchasePage'));
const SubscriptionSuccessPage = React.lazy(() => import('./components/provider/SubscriptionSuccessPage'));
const SubscriptionCancelPage = React.lazy(() => import('./components/provider/SubscriptionCancelPage'));
const CreditPurchaseSuccessPage = React.lazy(() => import('./components/provider/CreditPurchaseSuccessPage'));
const CreditPurchaseCancelPage = React.lazy(() => import('./components/provider/CreditPurchaseCancelPage'));
const ServiceBrowsingPage = React.lazy(() => import('./components/ServiceBrowsingPage'));
const TermsOfUsePage = React.lazy(() => import('./components/legal/TermsOfUsePage'));
const PrivacyPolicyPage = React.lazy(() => import('./components/legal/PrivacyPolicyPage'));
const CreateGroupPage = React.lazy(() => import('./components/group-buy/CreateGroupPage'));
const JoinGroupPage = React.lazy(() => import('./components/group-buy/JoinGroupPage'));
const GroupDetailPage = React.lazy(() => import('./components/group-buy/GroupDetailPage'));
const GroupMemberJobPage = React.lazy(() => import('./components/group-buy/GroupMemberJobPage'));
const GroupChatPage = React.lazy(() => import('./components/group-buy/GroupChatPage'));
const PublicJobsPage = React.lazy(() => import('./components/public/PublicJobsPage'));
const OpenGroupsPage = React.lazy(() => import('./components/public/OpenGroupsPage'));
const BlogListPage = React.lazy(() => import('./components/public/BlogListPage'));
const BlogDetailPage = React.lazy(() => import('./components/public/BlogDetailPage'));

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-600"></div>
  </div>
);

// Old links to the standalone Group Buy bid page now open the bid experience
// in the provider dashboard, keeping providers in their normal workspace.
const LegacyGroupBidRedirect = () => {
  const { id } = useParams();
  return <Navigate replace to={`/provider-dashboard?tab=groups&bid=${id}`} />;
};

const ErrorFallback = ({ error, retry }) => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-2xl font-bold text-red-600 mb-4">
        Something went wrong
      </h1>
      <p className="text-gray-600 mb-4">{error}</p>
      <button
        onClick={retry}
        className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700"
      >
        Try Again
      </button>
    </div>
  </div>
);

const LandingPage = () => {
  const { content, landingHero, error, source, refresh } = useWebsiteContent();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const heroRef = React.useRef(null);
  const [isPostJobModalOpen, setIsPostJobModalOpen] = React.useState(false);

  const isPrelaunchMode = process.env.REACT_APP_PRELAUNCH_MODE === 'true';

  const handlePostProject = () => {
    if (isAuthenticated) {
      setIsPostJobModalOpen(true);
    } else {
      navigate('/signin?redirect=/');
    }
  };

  if (error && !content) {
    return <ErrorFallback error={error} retry={refresh} />;
  }

  const meta = {
    title: 'Fix Directly - Connect with Top-Rated Home Repair Experts',
    description:
      'Need a home repair or improvement pro? Fix Directly connects you with trusted, vetted local contractors fast. Get matched and see if you qualify today.',
    keywords:
      'FixDirectly, home services marketplace, trusted home service professionals, home repair services, home improvement services, kitchen remodeling, bathroom repairs, electrical work, painting and decorating, general repairs',
  };
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://fixdirectly.com';
  const canonical = `${origin}/`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${origin}/#organization`,
        name: 'FixDirectly',
        url: `${origin}/`,
        logo: {
          '@type': 'ImageObject',
          url: `${origin}/favicons/android-chrome-512x512.png`,
        },
        description:
          'FixDirectly is a trusted home services marketplace that connects homeowners with verified local professionals for repairs, maintenance, remodeling, cleaning, installation, and other home improvement services.',
        foundingDate: '2025',
        areaServed: {
          '@type': 'Country',
          name: 'United States',
        },
        knowsAbout: [
          'Home Services',
          'Home Repairs',
          'Plumbing',
          'Electrical',
          'HVAC',
          'Roofing',
          'Painting',
          'Cleaning Services',
          'Flooring',
          'Landscaping',
          'Handyman Services',
          'Moving Services',
          'Appliance Repair',
          'Remodeling',
        ],
      },
      {
        '@type': 'WebSite',
        '@id': `${origin}/#website`,
        url: `${origin}/`,
        name: 'FixDirectly',
        publisher: {
          '@id': `${origin}/#organization`,
        },
        inLanguage: 'en-US',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${origin}/services?category={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${origin}/#webpage`,
        url: `${origin}/`,
        name: meta.title,
        description: meta.description,
        isPartOf: {
          '@id': `${origin}/#website`,
        },
        about: {
          '@id': `${origin}/#organization`,
        },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: `${origin}/og-share.png`,
        },
        breadcrumb: {
          '@id': `${origin}/#breadcrumb`,
        },
        inLanguage: 'en-US',
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${origin}/#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: `${origin}/`,
          },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content={meta.keywords} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      {/* Debug info for development */}
      {process.env.NODE_ENV === 'development' && (
        <div
          className={`text-xs p-2 text-center ${source === 'api'
            ? 'bg-green-100 text-green-800'
            : source === 'cache'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-yellow-100 text-yellow-800'
            }`}
        >
          <span className="font-mono">
            Content:{' '}
            {source === 'api'
              ? '🌐 API'
              : source === 'cache'
                ? '💾 Cache'
                : '🔧 Default'}{' '}
            | Version: {content?.version} | API:{' '}
            {process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000'}
          </span>
          <button
            onClick={refresh}
            className="ml-2 text-current underline hover:no-underline"
          >
            Refresh
          </button>
          {source === 'default' && (
            <span className="ml-2 text-current">(API server not running)</span>
          )}
        </div>
      )}

      <Header />
      <HotPostcodesTicker />
      <SplitHeroSection
        content={content?.sections?.split_hero}
        isModalOpen={isPostJobModalOpen}
        onOpenModal={() => setIsPostJobModalOpen(true)}
        onCloseModal={() => setIsPostJobModalOpen(false)}
      />
      {!isPrelaunchMode && <ProviderSearchSection />}
      {/* Legacy Hero Section - uncomment to restore original design
      <div ref={heroRef}>
        <HeroSectionWithModalLegacy content={content?.sections?.hero} />
      </div>
      */}
      <HowItWorksSection
        content={content?.sections?.how_it_works}
        onPostProjectClick={handlePostProject}
      />
      {!isPrelaunchMode && <GroupBuyPromo />}
      <BenefitsSection
        content={content?.sections?.benefits}
        onPostProjectClick={handlePostProject}
      />
      {!isPrelaunchMode && (
        <>
          <ServicesSection
            content={content?.sections?.services_grid}
            onPostProjectClick={handlePostProject}
          />
          <TestimonialsSection
            content={content?.sections?.testimonials}
            onPostProjectClick={handlePostProject}
          />
        </>
      )}
      <FAQSection content={content?.sections?.faq} />
      <Footer content={content?.sections?.footer} />
    </div>
  );
};

// Lives inside <BrowserRouter> so it can use useNavigate to handle notification clicks
function NotificationNavigator() {
  const navigate = useNavigate();
  React.useEffect(() => {
    const handler = (e) => {
      const { url, data = {} } = e.detail || {};
      if (!url) return;
      let normalized = url
        .replace(/^\/customer\/inquiries\//, '/customer/projects/')
        .replace(/^\/provider\/inquiries\//, '/provider/projects/');
      const bookingMatch = normalized.match(/^\/(customer|provider)\/bookings\/\d+/);
      if (bookingMatch) {
        const role = bookingMatch[1];
        const inquiryId = data.inquiry_id || data.project_id;
        normalized = inquiryId
          ? `/${role}/projects/${inquiryId}`
          : (role === 'customer' ? '/customer-dashboard?tab=projects' : '/provider-dashboard?tab=projects');
      }
      navigate(normalized);
    };
    window.addEventListener('notification:navigate', handler);
    return () => window.removeEventListener('notification:navigate', handler);
  }, [navigate]);
  return null;
}

function App() {
  const { toast } = useToast();
  useNotifications();

  React.useEffect(() => {
    const handleShowToast = (e) => {
      const { message, type, duration } = e.detail || {};
      toast({
        title: message,
        variant: type === 'warning' || type === 'error' ? 'destructive' : 'default',
        duration: duration || 5000,
      });
    };
    window.addEventListener('show-toast', handleShowToast);
    return () => window.removeEventListener('show-toast', handleShowToast);
  }, []);

  // Check if in prelaunch mode
  const isPrelaunchMode = process.env.REACT_APP_PRELAUNCH_MODE === 'true';

  // Prelaunch redirect component
  const PrelaunchRedirect = () => {
    const navigate = React.useCallback((path) => {
      window.location.href = '/';
    }, []);

    React.useEffect(() => {
      navigate('/');
    }, [navigate]);

    return <LoadingSpinner />;
  };

  return (
    <HelmetProvider>
    <FeatureFlagProvider>
      <PasswordGate>
        <div className="App">
          <Toaster />
          <BrowserRouter>
            <NotificationNavigator />
            <React.Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/services" element={<ServiceBrowsingPage />} />
              {/* SEO category landing pages — same component, clean path URLs.
                  postcode is optional: /services/plumbing or /services/plumbing/98005 */}
              <Route path="/services/:category" element={<ServiceBrowsingPage />} />
              <Route path="/services/:category/:postcode" element={<ServiceBrowsingPage />} />
              <Route path="/jobs" element={<PublicJobsPage />} />
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/:slug" element={<BlogDetailPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/partner" element={<PartnerPage />} />
              <Route path="/group-buy" element={<GroupBuyInfoPage />} />
              <Route path="/share/postcode/:postcode/:serviceId" element={<PostcodeSharePage />} />
              <Route path="/share/:token" element={<ShareLinkPage />} />
              <Route path="/groups" element={<OpenGroupsPage />} />
              <Route path="/groups/join/:token" element={<JoinGroupPage />} />
              <Route
                path="/groups/new"
                element={
                  <ProtectedRoute>
                    <CreateGroupPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/groups/:id/bid"
                element={
                  <ProtectedRoute requiredRoles={['provider']}>
                    <LegacyGroupBidRedirect />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/groups/:id/chat"
                element={
                  <ProtectedRoute>
                    <GroupChatPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customer/projects/group-buy/:id"
                element={
                  <ProtectedRoute requiredRoles={['customer']}>
                    <GroupMemberJobPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/groups/:id"
                element={
                  <ProtectedRoute>
                    <GroupDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customer-dashboard"
                element={
                  <ProtectedRoute requiredRoles={['customer']}>
                    <CustomerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/provider-dashboard"
                element={
                  <ProtectedRoute requiredRoles={['provider']}>
                    <CreditsProvider>
                      <ProviderDashboard />
                    </CreditsProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/provider/membership-plans"
                element={
                  <ProtectedRoute requiredRoles={['provider']}>
                    <CreditsProvider>
                      <ProviderMembershipPlans />
                    </CreditsProvider>
                  </ProtectedRoute>
                }
              />
              {/* Credit Purchase Routes */}
              <Route
                path="/provider/credits"
                element={
                  <ProtectedRoute requiredRoles={['provider']}>
                    <CreditsProvider>
                      <CreditPurchasePage />
                    </CreditsProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/provider/credits/success"
                element={
                  <ProtectedRoute requiredRoles={['provider']}>
                    <CreditPurchaseSuccessPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/provider/credits/cancel"
                element={
                  <ProtectedRoute requiredRoles={['provider']}>
                    <CreditPurchaseCancelPage />
                  </ProtectedRoute>
                }
              />
              {/* Subscription Success/Cancel Routes */}
              <Route
                path="/provider/subscription/success"
                element={
                  <ProtectedRoute requiredRoles={['provider']}>
                    <SubscriptionSuccessPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/provider/subscription/cancel"
                element={
                  <ProtectedRoute requiredRoles={['provider']}>
                    <SubscriptionCancelPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/project/:projectId"
                element={
                  <ProtectedRoute>
                    <ProjectDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/provider/projects/:projectId"
                element={
                  <ProtectedRoute requiredRoles={['provider']}>
                    <ProviderProjectDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customer/projects/:projectId"
                element={
                  <ProtectedRoute requiredRoles={['customer']}>
                    <CustomerProjectDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="/provider/:providerId" element={<ProviderProfile />} />
              {/* Compatibility for Group Buy links created before the public
                  profile URL was standardized to /provider/:providerId. */}
              <Route path="/providers/:providerId/profile" element={<ProviderProfile />} />
              {/* Legal Pages */}
              <Route path="/terms-of-use" element={<TermsOfUsePage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/signin" element={<SigninPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<OtpVerificationPage />} />
              {/* Routes disabled in prelaunch mode */}
              {isPrelaunchMode ? (
                <>
                  {/* Redirect all other routes to home in prelaunch mode */}
                  <Route path="/services" element={<PrelaunchRedirect />} />
                  <Route path="/contact" element={<PrelaunchRedirect />} />
                  <Route path="/customer-dashboard" element={<PrelaunchRedirect />} />
                  <Route path="/provider-dashboard" element={<PrelaunchRedirect />} />
                  <Route path="/provider/membership-plans" element={<PrelaunchRedirect />} />
                  <Route path="/project/:projectId" element={<PrelaunchRedirect />} />
                  <Route path="/provider/projects/:projectId" element={<PrelaunchRedirect />} />
                  <Route path="/customer/projects/:projectId" element={<PrelaunchRedirect />} />
                  <Route path="/provider/:providerId" element={<PrelaunchRedirect />} />

                  {/* Admin route - always accessible even in prelaunch */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                </>
              ) : (
                <>
                  {/* Full routes when not in prelaunch mode */}
                  <Route path="/services" element={<ServiceBrowsingPage />} />
                  <Route path="/jobs" element={<PublicJobsPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route
                    path="/customer-dashboard"
                    element={
                      <ProtectedRoute requiredRoles={['customer']}>
                        <CustomerDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/provider-dashboard"
                    element={
                      <ProtectedRoute requiredRoles={['provider']}>
                        <CreditsProvider>
                          <ProviderDashboard />
                        </CreditsProvider>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/provider/membership-plans"
                    element={
                      <ProtectedRoute requiredRoles={['provider']}>
                        <CreditsProvider>
                          <ProviderMembershipPlans />
                        </CreditsProvider>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/project/:projectId"
                    element={
                      <ProtectedRoute>
                        <ProjectDetailsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/provider/projects/:projectId"
                    element={
                      <ProtectedRoute requiredRoles={['provider']}>
                        <ProviderProjectDetailsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/customer/projects/:projectId"
                    element={
                      <ProtectedRoute requiredRoles={['customer']}>
                        <CustomerProjectDetailsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/provider/:providerId" element={<ProviderProfile />} />
                </>
              )}
            </Routes>
            </React.Suspense>
          </BrowserRouter>
        </div>
      </PasswordGate>
    </FeatureFlagProvider>
    </HelmetProvider>
  );
}

export default App;
